import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Users, BookOpen, Loader2, UserPlus, Search, X, GraduationCap, Boxes, Pencil, Trash2,
} from "lucide-react";
import { GroupFormDialog } from "@/components/GroupFormDialog";
import MinhasAlunas from "@/components/MinhasAlunas";

import { confirmar } from "@/components/ConfirmDialog";
export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backPath = isAdmin ? "/admin/turmas" : "/prof/turmas";

  const [group, setGroup] = useState(null);
  const [findQ, setFindQ] = useState("");
  const [studentsDialog, setStudentsDialog] = useState(false);
  const [coursesDialog, setCoursesDialog] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [selStudents, setSelStudents] = useState([]);
  const [selCourses, setSelCourses] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const removeGroup = async () => {
    if (!(await confirmar(`Excluir a turma "${group.name}"? Os alunos não são apagados, só saem da turma.`))) return;
    await api.delete(`/groups/${id}`);
    toast.success("Turma excluída.");
    navigate(backPath);
  };

  const load = () => api.get(`/groups/${id}`).then((r) => setGroup(r.data)).catch(() => navigate(backPath));
  useEffect(() => { load(); }, [id]);

  const openStudents = async () => {
    const res = await api.get("/students");
    setAllStudents(res.data);
    setSelStudents(group.students.map((s) => s.id));
    setStudentsDialog(true);
  };
  const openCourses = async () => {
    const res = await api.get("/courses");
    setAllCourses(res.data);
    setSelCourses(group.courses.map((c) => c.id));
    setCoursesDialog(true);
  };

  const saveStudents = async (ids) => {
    setSaving(true);
    try {
      const res = await api.put(`/groups/${id}/students`, { ids });
      setGroup(res.data);
      setStudentsDialog(false);
      toast.success("Alunos da turma atualizados.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeStudent = async (sid) => {
    const ids = group.students.map((s) => s.id).filter((x) => x !== sid);
    await saveStudents(ids);
  };

  const saveCourses = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/groups/${id}/courses`, { ids: selCourses });
      setGroup(res.data);
      setCoursesDialog(false);
      toast.success("Matrícula da turma atualizada.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!group) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;

  const filtered = allStudents.filter((s) => !q || s.name?.toLowerCase().includes(q.toLowerCase()) || s.email?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-up">
      <button onClick={() => navigate(backPath)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl grid place-items-center text-white shrink-0" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
            <Boxes className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-bold text-slate-900">{group.name}
              {group.acesso === "consulta" && <span className="ml-2 align-middle text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">só consulta</span>}</h1>
            {group.description && <p className="text-slate-500 text-sm mt-0.5">{group.description}</p>}
            <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
              <GraduationCap className="h-4 w-4 text-[#1E9E2A]" />
              {group.instructor_name || "Sem professor atribuído"}
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="btn-edit-group"><Pencil className="h-4 w-4 mr-1.5" /> Editar turma</Button>
              <Button variant="outline" onClick={removeGroup} className="text-red-500 hover:text-red-600" title="Excluir turma"><Trash2 className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      {/* Cursos das alunas (pelas matrículas de cada uma) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><BookOpen className="h-4 w-4" /> Cursos das alunas</h2>
          <p className="text-xs text-slate-400 mt-0.5">Clique no curso para ver o acompanhamento de cada aluna: progresso, tempo, último acesso e de onde.</p>
        </div>
        <div className="p-5">
          {group.courses.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhuma aluna desta turma tem matrícula em curso.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {group.courses.map((c) => (
                <button key={c.id} onClick={() => navigate(`${isAdmin ? "/admin" : "/prof"}/turmas/${id}/curso/${c.id}`)} data-testid={`group-course-${c.id}`}
                  className="text-left rounded-xl border border-slate-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all bg-white">
                  <div className="aspect-[3/1] bg-slate-100">{c.banner ? <img src={c.banner} alt="" className="h-full w-full object-cover" /> : null}</div>
                  <div className="p-3">
                    <div className="font-semibold text-sm text-slate-800 line-clamp-2">{c.title}</div>
                    <div className="text-sm text-slate-700 mt-1"><strong className="text-[#1E9E2A]">{c.active}</strong> {c.active === 1 ? "aluno ativo" : "alunos ativos"}</div>
                    {c.students !== c.active && <div className="text-xs text-slate-400">+ {c.students - c.active} com matrícula vencida ou bloqueada</div>}
                    <div className="text-xs font-semibold text-[#1E9E2A] mt-2">Ver acompanhamento →</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alunos: a professora vê a lista completa (só consulta); o admin gerencia */}
      {!isAdmin ? <MinhasAlunas grupo={id} /> : (<>
      {/* Alunos */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><Users className="h-4 w-4" /> Alunos ({group.students.length})</h2>
          {isAdmin && (
            <Button data-testid="btn-manage-group-students" size="sm" variant="outline" onClick={openStudents}><UserPlus className="h-4 w-4 mr-1.5" /> Gerenciar alunos</Button>
          )}
        </div>
        <div className="p-3">
          {group.students.length === 0 ? (
            <p className="text-sm text-slate-400 p-2">Nenhum aluno nesta turma.</p>
          ) : (<>
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={findQ} onChange={(e) => setFindQ(e.target.value)} placeholder="Digite o nome, e-mail ou ID do aluno..." className="pl-9 h-11" data-testid="group-student-search" />
            </div>
            {(() => {
              const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
              const t = norm(findQ.trim());
              if (t.length < 2) return <p className="text-xs text-slate-400 px-1 pt-2">Digite ao menos 2 letras para encontrar um aluno.</p>;
              const found = group.students.filter((s) => norm(`${s.name} ${s.email} ${s.reg_code || ""}`).includes(t));
              if (!found.length) return <p className="text-sm text-slate-400 px-1 pt-3">Nenhum aluno encontrado.</p>;
              return (<>
            <ul className="divide-y divide-slate-100 mt-2">
              {found.slice(0, 30).map((s) => (
                <li key={s.id} data-testid={`group-student-${s.id}`} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="h-8 w-8 rounded-full grid place-items-center text-white text-xs font-bold" style={{ background: "#1E9E2A" }}>
                    {s.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
                    <div className="text-xs text-slate-400 truncate">{s.email}</div>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.status === "cadastrado" ? "bg-teal-50 text-teal-700" : s.status === "importado" ? "bg-sky-50 text-sky-700" : "bg-orange-50 text-orange-700"}`}>
                    {s.status === "cadastrado" ? "Já acessou" : s.status === "importado" ? "Ainda não acessou" : "Aguardando 1º acesso"}
                  </span>
                  <button onClick={() => removeStudent(s.id)} className="text-slate-300 hover:text-red-500 p-1" title="Tirar da turma"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
            {found.length > 30 && <p className="text-xs text-slate-400 px-1 pt-2">Mostrando 30 de {found.length}. Continue digitando para refinar.</p>}
              </>);
            })()}
          </>)}
        </div>
      </div>

      </>)}

      {/* Students dialog */}
      <Dialog open={studentsDialog} onOpenChange={setStudentsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Alunos da turma</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluno..." className="pl-9" />
          </div>
          <div className="max-h-72 overflow-y-auto iep-scroll space-y-1 py-1">
            {filtered.map((s) => {
              const checked = selStudents.includes(s.id);
              return (
                <label key={s.id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={checked} onCheckedChange={(v) => setSelStudents((p) => v ? [...p, s.id] : p.filter((x) => x !== s.id))} />
                  <div className="flex-1 min-w-0"><div className="text-sm text-slate-700 truncate">{s.name}</div><div className="text-xs text-slate-400 truncate">{s.email}</div></div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => saveStudents(selStudents)} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Salvar (${selStudents.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Courses dialog */}
      <Dialog open={coursesDialog} onOpenChange={setCoursesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Matricular turma em cursos</DialogTitle></DialogHeader>
          <div className="max-h-72 overflow-y-auto iep-scroll space-y-1 py-1">
            {allCourses.map((c) => {
              const checked = selCourses.includes(c.id);
              return (
                <label key={c.id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={checked} onCheckedChange={(v) => setSelCourses((p) => v ? [...p, c.id] : p.filter((x) => x !== c.id))} />
                  <div className="text-sm text-slate-700">{c.title}</div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button data-testid="save-group-courses" onClick={saveCourses} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Salvar (${selCourses.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {isAdmin && <GroupFormDialog open={editOpen} onOpenChange={setEditOpen} group={group} onSaved={() => load()} />}
    </div>
  );
}
